import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ActiveFilters from './ActiveFilters';
import Actions from './Actions';
import Icon from '@components/custom/Icon';
import ModuleHeader from '@components/ModuleHeader';
import Spinner from '@components/custom/Spinner';
import Table from '@components/custom/Table';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';
import { getCleanArray } from '@sm/utils/utils';

const PayinMethods = ({ type }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showTable, setShowTable] = useState(true);
  
  const [tableData, setTableData] = useState([]);

  const isPayin = type === 'payin';

  const currencyKey = `currency-${type}`;
  const methodsKey = `methods-${type}`;
  const trafficKey = `trafficTypes-${type}`;
  const availabilityKey = `availability-${type}`;

  const [selectedOptions, setSelectedOptions] = useState({
    [currencyKey]: ['-1'],
    [methodsKey]: ['-1'],
    [trafficKey]: [],
    [availabilityKey]: []
  });

  const {
    currencies,
    payinMethods,
    payoutMethods,
    trafficTypes,
    availability,
    filtersReady
  } = useSelector((state) => state.dashboard);

  const filters = useMemo(() => [
    { id: currencyKey, label: 'Currency', options: currencies },
    { id: methodsKey, label: 'Method', options: isPayin ? payinMethods : payoutMethods },
    ...(isPayin ? [{ id: trafficKey, label: 'Traffic', options: trafficTypes }] : []),
    { id: availabilityKey, label: 'Availability', options: availability }
  ], [currencies, payinMethods, payoutMethods, trafficTypes, availability, isPayin, currencyKey, methodsKey, trafficKey, availabilityKey]);

  const columns = useMemo(() => [
    {
      id: currencyKey,
      value: "currency",
      label: "Currency",
      leftAligned: true,
      isNumber: false
    },
    {
      id: methodsKey,
      value: "method",
      label: "Method",
      leftAligned: true,
      isNumber: false
    },
    ...(isPayin ? [{
      id: trafficKey,
      value: "traffic",
      label: "Traffic",
      leftAligned: true,
      isNumber: false
    }] : []),
    {
      id: availabilityKey,
      value: 'availability',
      label: 'Availability',
      rightAligned: true,
      isNumber: false
    }
  ], [isPayin, currencyKey, methodsKey, trafficKey, availabilityKey]);

  const getFilteredData = useCallback(() => {
    const params = [
      `currencies=${getCleanArray(selectedOptions[currencyKey]).join(',')}`,
      `methods=${getCleanArray(selectedOptions[methodsKey]).join(',')}`,
      `trafficTypes=${selectedOptions[trafficKey].join(',')}`,
      `includeEnabled=${selectedOptions[availabilityKey].includes('Online')}`,
      `includeDisabled=${selectedOptions[availabilityKey].includes('Offline')}`,
    ].join('&');

    crudActions.get(`v3/clients/dashboard/${type}-methods?${params}`)
      .then(response => {
        setTableData(response);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        dispatch(showAlert('danger', err?.message));
      });
  }, [dispatch, selectedOptions, currencyKey, methodsKey, trafficKey, availabilityKey, type]);

  useEffect(() => {
    if (!filtersReady) return;

    setIsLoading(true);
    getFilteredData();
  }, [filtersReady, getFilteredData]);

  const handleCheckboxChange = useCallback((group, value, event) => {
    const isChecked = event.target.checked;

    setSelectedOptions(prev => {
      const currentGroup = prev[group] || [];
      
      const updatedGroup = isChecked
        ? value === '-1' ? [value] : [...currentGroup.filter(item => item !== '-1'), value]
        : currentGroup.filter(item => item !== value);

      return {
        ...prev,
        [group]: updatedGroup,
      };
    });
  }, []);

  const prepareTableData = useMemo(() => {
    return tableData.map(elem => {
      return {
        ...elem,
        disabledRow: !elem.available,
        availability: (
          <div className='d-flex align-items-center justify-content-end availability-wrap'>
            <Icon name='mode_off_on' size={ 20 } color={ elem.available ? '#2d2d2d' : '#dbdbdb' } />
            <span className={ elem.available ? 'on' : 'off' }> {elem.available ? 'Online' : 'Offline'} </span>
          </div>
        )
      };
    });
  }, [tableData]);

  const removeCategoryFilter = useCallback((category) => {
    if (!category) return;
    const resetArray = [];
    if (category.includes('currency') || category.includes('method')) {
      resetArray.push('-1');
    }
    setSelectedOptions(prev => ({ ...prev, [category]: resetArray }));
  }, []);

  return (
    <div className='component shadow mb-4 position-relative'>
      <ModuleHeader
        id='credit_card'
        title={ `${isPayin ? 'Payin' : 'Payout'} Methods` }
        actions={
          <Actions
            type={ type }
            filters={ filters }
            selectedOptions={ selectedOptions }
            showTable={ showTable }
            setShowTable={ setShowTable }
            handleCheckboxChange={ handleCheckboxChange }
          />
        }
        filters={
          <ActiveFilters
            selectedOptions={ selectedOptions }
            removeCategoryFilter={ (key) => removeCategoryFilter(key) }
          />
        }
      />

      {isLoading && <Spinner /> }

      <Table
        className={ `overflow-table ${isPayin ? 'payin' : 'payout'} ${showTable ? 'show' : 'hidden'}` }
        columns={ columns }
        data={ prepareTableData }
        filters={ selectedOptions }
      />

    </div>
  );
};

export default PayinMethods;